import {
  Banknote,
  Building2,
  Check,
  Loader,
  UserCheck,
  Wallet,
  X,
} from "lucide-react";

import { formatDate } from "../Utils/dateFormatter";

const SettlementModals = ({
  // =====================================================
  // SETTLEMENT DETAIL MODAL
  // =====================================================

  selectedSettlement,
  setSelectedSettlement,

  modalAttendanceData,
  modalAttendanceLoading,

  modalWeeklyPoints,
  modalWeeklyPointsLoading,

  fromDate,
  toDate,

  formatAmount,
  getStatusMeta,

  // =====================================================
  // EDIT SETTLEMENT MODAL
  // =====================================================

  editSettlement,
  editSettlementAmount,
  setEditSettlementAmount,

  editPerformanceAmount,
  setEditPerformanceAmount,

  editDate,
  setEditDate,

  closeEditSettlementModal,
  handleUpdateSettlement,
  updateSettlementLoading,

  // =====================================================
  // ADD EMPLOYEE MODAL
  // =====================================================

  employeeModalOpen,
  setEmployeeModalOpen,
}) => {
  // =====================================================
  // COMMON HELPERS
  // =====================================================

  const getDateRangeLabel = (createdOn) => {
    if (!createdOn) return "-";

    const [start, end] = String(createdOn).split(":");

    if (!start || !end) return createdOn;

    return `${formatDate(start)} to ${formatDate(end)}`;
  };

  const formatDutyTime = (value) => {
    if (!value) return "-";

    try {
      return new Date(value).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "-";
    }
  };

  const getAttendanceStatusMeta = (status) => {
    const normalized = String(status || "").toLowerCase();

    if (normalized.includes("full")) {
      return {
        label: "Full Day",
        className: "bg-emerald-50 text-emerald-700 ring-emerald-200",
        dotClass: "bg-emerald-500",
      };
    }

    if (normalized.includes("half")) {
      return {
        label: "Half Day",
        className: "bg-yellow-50 text-yellow-700 ring-yellow-200",
        dotClass: "bg-yellow-500",
      };
    }

    if (normalized.includes("pending")) {
      return {
        label: "Pending",
        className: "bg-orange-50 text-orange-700 ring-orange-200",
        dotClass: "bg-orange-500",
      };
    }

    if (normalized.includes("absent")) {
      return {
        label: "Absent",
        className: "bg-red-50 text-red-700 ring-red-200",
        dotClass: "bg-red-500",
      };
    }

    return {
      label: status || "-",
      className: "bg-gray-50 text-gray-700 ring-gray-200",
      dotClass: "bg-gray-400",
    };
  };

  // =====================================================
  // ATTENDANCE DATA
  // =====================================================

  const selectedDriverAttendance = modalAttendanceData?.[0] || null;

  // =====================================================
  // WEEKLY PERFORMANCE POINTS
  // =====================================================

  const totalModalWeeklyPoints = (modalWeeklyPoints || []).reduce(
    (sum, item) => sum + Number(item?.dailyPoints || 0),
    0,
  );

  const latestModalWeeklyPoint =
    modalWeeklyPoints?.length > 0
      ? modalWeeklyPoints[modalWeeklyPoints.length - 1]
      : null;

  const modalWeeklyTotalPoints =
    latestModalWeeklyPoint?.totalPoints ?? totalModalWeeklyPoints;

  return (
    <>
      {/* =====================================================
          SETTLEMENT DETAIL MODAL
      ===================================================== */}

      {selectedSettlement && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm sm:p-5"
          onClick={() => setSelectedSettlement(null)}
        >
          <div
            className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-white/20 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}

            <div className="flex shrink-0 items-center justify-between border-b border-orange-100 bg-gradient-to-r from-orange-500 to-orange-400 px-4 py-3 text-white sm:px-5">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/20">
                    <Wallet size={18} />
                  </div>

                  <div className="min-w-0">
                    <h3 className="truncate text-base font-black sm:text-lg">
                      Settlement Details
                    </h3>

                    <p className="mt-0.5 truncate text-xs text-orange-50">
                      {selectedSettlement.name || "-"} ·{" "}
                      {selectedSettlement.driver_code || "-"}
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setSelectedSettlement(null)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/30"
                aria-label="Close settlement modal"
              >
                <X size={19} />
              </button>
            </div>

            {/* Body */}

            <div className="flex-1 overflow-y-auto bg-gray-50/80 p-3 sm:p-4">
              {/* =====================================================
                  TOP SUMMARY
              ===================================================== */}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {/* Settlement Amount */}

                <div className="rounded-2xl border border-orange-100 bg-gradient-to-br from-orange-50 to-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-orange-600">
                        Settlement Amount
                      </p>

                      <p className="mt-2 text-2xl font-black text-gray-900">
                        {formatAmount(selectedSettlement.total_amount)}
                      </p>
                    </div>

                    <div className="rounded-xl bg-orange-500 p-2 text-white">
                      <Banknote size={18} />
                    </div>
                  </div>

                  <div className="mt-3 inline-flex rounded-full border border-orange-100 bg-white px-2.5 py-1 text-[11px] font-bold text-orange-700">
                    {getDateRangeLabel(selectedSettlement.created_on)}
                  </div>
                </div>

                {/* Driver */}

                <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                        Driver
                      </p>

                      <p className="mt-2 truncate text-sm font-black text-gray-900">
                        {selectedSettlement.name || "-"}
                      </p>

                      <p className="mt-1 text-xs font-bold text-orange-600">
                        {selectedSettlement.driver_code || "-"}
                      </p>
                    </div>

                    <div className="rounded-xl bg-blue-50 p-2 text-blue-600">
                      <UserCheck size={18} />
                    </div>
                  </div>
                </div>

                {/* Status */}

                <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                    Settlement Status
                  </p>

                  <div className="mt-3">
                    {(() => {
                      const statusMeta = getStatusMeta(
                        selectedSettlement.status,
                      );

                      return (
                        <span
                          className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold ring-1 ${statusMeta.className}`}
                        >
                          <span
                            className={`h-2 w-2 rounded-full ${
                              String(
                                selectedSettlement.status || "",
                              ).toUpperCase() === "Y"
                                ? "bg-emerald-500"
                                : "bg-orange-500"
                            }`}
                          />

                          {statusMeta.label}
                        </span>
                      );
                    })()}
                  </div>
                </div>

                {/* Weekly Points */}

                <div className="rounded-2xl border border-violet-100 bg-gradient-to-br from-violet-50 to-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-violet-600">
                        Weekly Points
                      </p>

                      {modalWeeklyPointsLoading ? (
                        <div className="mt-3 h-8 w-20 animate-pulse rounded-lg bg-violet-100" />
                      ) : (
                        <p className="mt-2 text-2xl font-black text-gray-900">
                          {modalWeeklyTotalPoints}

                          <span className="ml-1 text-xs font-bold text-gray-400">
                            pts
                          </span>
                        </p>
                      )}
                    </div>

                    <div className="rounded-xl bg-violet-500 p-2 text-white">
                      <Check size={18} />
                    </div>
                  </div>

                  <p className="mt-3 text-[11px] font-medium text-gray-500">
                    {formatDate(fromDate)} to {formatDate(toDate)}
                  </p>
                </div>
              </div>

              {/* =====================================================
                  DRIVER + ATTENDANCE
              ===================================================== */}

              <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-[300px_minmax(0,1fr)]">
                {/* =================================================
                    LEFT DETAILS
                ================================================= */}

                <div className="space-y-3">
                  {/* DRIVER DETAILS */}

                  <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <div className="flex items-center gap-2 border-b bg-gray-50 px-4 py-3">
                      <UserCheck size={16} className="text-orange-500" />

                      <h4 className="text-sm font-black text-gray-900">
                        Driver Information
                      </h4>
                    </div>

                    <div className="divide-y divide-gray-100 px-4">
                      <div className="flex items-start justify-between gap-4 py-3">
                        <p className="text-xs font-semibold text-gray-400">
                          Driver Name
                        </p>

                        <p className="max-w-[160px] text-right text-xs font-bold text-gray-900">
                          {selectedSettlement.name || "-"}
                        </p>
                      </div>

                      <div className="flex items-start justify-between gap-4 py-3">
                        <p className="text-xs font-semibold text-gray-400">
                          Driver Code
                        </p>

                        <p className="text-right text-xs font-black text-orange-600">
                          {selectedSettlement.driver_code || "-"}
                        </p>
                      </div>

                      <div className="flex items-start justify-between gap-4 py-3">
                        <p className="text-xs font-semibold text-gray-400">
                          Settlement Period
                        </p>

                        <p className="max-w-[170px] text-right text-xs font-bold text-gray-700">
                          {getDateRangeLabel(selectedSettlement.created_on)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* BANK DETAILS */}

                  <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <div className="flex items-center gap-2 border-b bg-gray-50 px-4 py-3">
                      <Building2 size={16} className="text-orange-500" />

                      <h4 className="text-sm font-black text-gray-900">
                        Bank Information
                      </h4>
                    </div>

                    <div className="divide-y divide-gray-100 px-4">
                      <div className="py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                          Bank Name
                        </p>

                        <p className="mt-1 break-words text-xs font-bold text-gray-900">
                          {selectedSettlement.bank_name || "-"}
                        </p>
                      </div>

                      <div className="py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                          Account Holder
                        </p>

                        <p className="mt-1 break-words text-xs font-bold text-gray-900">
                          {selectedSettlement.bank_holder_name || "-"}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 gap-3 py-3 sm:grid-cols-2 lg:grid-cols-1">
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                            Account Number
                          </p>

                          <p className="mt-1 break-all text-xs font-bold text-gray-900">
                            {selectedSettlement.account_no || "-"}
                          </p>
                        </div>

                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                            IFSC Code
                          </p>

                          <p className="mt-1 break-all font-mono text-xs font-bold text-gray-900">
                            {selectedSettlement.ifsc_code || "-"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* =================================================
                    WEEKLY ATTENDANCE
                ================================================= */}

                <div className="min-w-0 overflow-hidden rounded-2xl border border-orange-100 bg-white shadow-sm">
                  <div className="flex flex-col gap-2 border-b bg-gradient-to-r from-orange-50 to-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <UserCheck size={17} className="text-orange-500" />

                        <h4 className="text-sm font-black text-gray-900">
                          Weekly Attendance
                        </h4>
                      </div>

                      <p className="mt-1 text-[11px] text-gray-500">
                        Attendance records from {formatDate(fromDate)} to{" "}
                        {formatDate(toDate)}
                      </p>
                    </div>

                    {modalAttendanceLoading && (
                      <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-bold text-orange-600 shadow-sm ring-1 ring-orange-100">
                        <Loader size={13} className="animate-spin" />
                        Loading
                      </div>
                    )}
                  </div>

                  {/* LOADING */}

                  {modalAttendanceLoading ? (
                    <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
                      {Array.from({ length: 4 }).map((_, index) => (
                        <div
                          key={index}
                          className="rounded-xl border border-gray-100 p-3"
                        >
                          <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />

                          <div className="mt-3 h-3 w-32 animate-pulse rounded bg-gray-100" />

                          <div className="mt-3 h-12 w-full animate-pulse rounded bg-gray-100" />
                        </div>
                      ))}
                    </div>
                  ) : !selectedDriverAttendance?.attendance?.length ? (
                    /* EMPTY */

                    <div className="flex min-h-[280px] items-center justify-center p-6 text-center">
                      <div>
                        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-50 text-orange-500">
                          <UserCheck size={25} />
                        </div>

                        <p className="mt-3 text-sm font-black text-gray-900">
                          No attendance found
                        </p>

                        <p className="mt-1 text-xs text-gray-500">
                          No attendance record is available for this week.
                        </p>
                      </div>
                    </div>
                  ) : (
                    /* DATA */

                    <div className="p-3">
                      {/* ATTENDANCE SUMMARY */}

                      <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                        {/* DAYS */}

                        <div className="rounded-xl border border-gray-100 bg-gray-50 p-2.5 text-center">
                          <p className="text-lg font-black text-gray-900">
                            {selectedDriverAttendance.attendance.length}
                          </p>

                          <p className="text-[10px] font-bold uppercase text-gray-400">
                            Days
                          </p>
                        </div>

                        {/* FULL */}

                        <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-2.5 text-center">
                          <p className="text-lg font-black text-emerald-700">
                            {
                              selectedDriverAttendance.attendance.filter(
                                (day) =>
                                  String(day.status || "")
                                    .toLowerCase()
                                    .includes("full"),
                              ).length
                            }
                          </p>

                          <p className="text-[10px] font-bold uppercase text-emerald-600">
                            Full
                          </p>
                        </div>

                        {/* PENDING */}

                        <div className="rounded-xl border border-orange-100 bg-orange-50 p-2.5 text-center">
                          <p className="text-lg font-black text-orange-700">
                            {
                              selectedDriverAttendance.attendance.filter(
                                (day) =>
                                  String(day.status || "")
                                    .toLowerCase()
                                    .includes("pending"),
                              ).length
                            }
                          </p>

                          <p className="text-[10px] font-bold uppercase text-orange-600">
                            Pending
                          </p>
                        </div>

                        {/* ABSENT */}

                        <div className="rounded-xl border border-red-100 bg-red-50 p-2.5 text-center">
                          <p className="text-lg font-black text-red-700">
                            {
                              selectedDriverAttendance.attendance.filter(
                                (day) =>
                                  String(day.status || "")
                                    .toLowerCase()
                                    .includes("absent"),
                              ).length
                            }
                          </p>

                          <p className="text-[10px] font-bold uppercase text-red-600">
                            Absent
                          </p>
                        </div>
                      </div>

                      {/* ATTENDANCE TABLE */}

                      <div className="max-h-[320px] overflow-auto rounded-xl border border-gray-200">
                        <table className="min-w-[760px] w-full text-xs">
                          <thead className="sticky top-0 z-10 bg-gray-100 text-gray-600">
                            <tr>
                              <th className="px-3 py-2 text-left font-bold">
                                Date
                              </th>

                              <th className="px-3 py-2 text-center font-bold">
                                Status
                              </th>

                              <th className="px-3 py-2 text-center font-bold">
                                Duty Hours
                              </th>

                              <th className="px-3 py-2 text-center font-bold">
                                On Duty
                              </th>

                              <th className="px-3 py-2 text-center font-bold">
                                Off Duty
                              </th>

                              <th className="px-3 py-2 text-center font-bold">
                                Duration
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {selectedDriverAttendance.attendance.map((day) => {
                              const statusMeta = getAttendanceStatusMeta(
                                day.status,
                              );

                              const formattedDate = day.date
                                ? formatDate(day.date)
                                : "-";

                              const firstTiming = day.timings?.[0] || null;

                              const extraTimingCount =
                                day.timings?.length > 1
                                  ? day.timings.length - 1
                                  : 0;

                              return (
                                <tr
                                  key={day.date}
                                  className="border-t border-gray-100 transition hover:bg-orange-50/40"
                                >
                                  <td className="px-3 py-2.5">
                                    <p className="font-bold text-gray-900">
                                      {formattedDate}
                                    </p>
                                  </td>

                                  <td className="px-3 py-2.5 text-center">
                                    <span
                                      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-bold ring-1 ${statusMeta.className}`}
                                    >
                                      <span
                                        className={`h-1.5 w-1.5 rounded-full ${statusMeta.dotClass}`}
                                      />

                                      {statusMeta.label}
                                    </span>
                                  </td>

                                  <td className="px-3 py-2.5 text-center font-bold text-gray-700">
                                    {day.totalDutyHours || "0h 0m"}
                                  </td>

                                  <td className="px-3 py-2.5 text-center text-gray-700">
                                    {firstTiming
                                      ? formatDutyTime(firstTiming.on_duty_time)
                                      : "-"}
                                  </td>

                                  <td className="px-3 py-2.5 text-center text-gray-700">
                                    {firstTiming
                                      ? formatDutyTime(
                                          firstTiming.off_duty_time,
                                        )
                                      : "-"}
                                  </td>

                                  <td className="px-3 py-2.5 text-center">
                                    <div className="inline-flex items-center gap-1.5">
                                      <span className="rounded-lg bg-orange-50 px-2 py-1 font-bold text-orange-700">
                                        {firstTiming?.duration ||
                                          "Running / Pending"}
                                      </span>

                                      {extraTimingCount > 0 && (
                                        <span className="rounded-full bg-gray-100 px-2 py-1 text-[10px] font-bold text-gray-600">
                                          +{extraTimingCount}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* =====================================================
                  WEEKLY PERFORMANCE POINTS
              ===================================================== */}

              <div className="mt-3 overflow-hidden rounded-2xl border border-violet-100 bg-white shadow-sm">
                <div className="flex flex-col gap-2 border-b bg-gradient-to-r from-violet-50 to-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h4 className="text-sm font-black text-gray-900">
                      Weekly Performance Points
                    </h4>

                    <p className="mt-1 text-[11px] text-gray-500">
                      Performance details from {formatDate(fromDate)} to{" "}
                      {formatDate(toDate)}
                    </p>
                  </div>

                  {modalWeeklyPointsLoading ? (
                    <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-violet-600 shadow-sm ring-1 ring-violet-100">
                      <Loader size={13} className="animate-spin" />
                      Loading points
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-2 rounded-xl bg-violet-500 px-3 py-1.5 text-xs font-black text-white shadow-sm">
                      <Check size={14} />
                      Total: {modalWeeklyTotalPoints} pts
                    </div>
                  )}
                </div>

                {modalWeeklyPointsLoading ? (
                  <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-3">
                    {[1, 2, 3].map((item) => (
                      <div
                        key={item}
                        className="h-16 animate-pulse rounded-xl bg-violet-50"
                      />
                    ))}
                  </div>
                ) : modalWeeklyPoints.length === 0 ? (
                  <div className="flex min-h-[120px] items-center justify-center p-5 text-center">
                    <div>
                      <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-violet-50 text-violet-500">
                        <Check size={20} />
                      </div>

                      <p className="mt-2 text-xs font-bold text-gray-800">
                        No weekly points found
                      </p>

                      <p className="mt-1 text-[11px] text-gray-500">
                        No performance points are available for this week.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-[650px] w-full text-left text-xs">
                      <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="px-4 py-3 font-bold">S/N</th>

                          <th className="px-4 py-3 font-bold">
                            Performance ID
                          </th>

                          <th className="px-4 py-3 text-center font-bold">
                            Total Points
                          </th>

                          <th className="px-4 py-3 text-center font-bold">
                            Daily Points
                          </th>

                          <th className="px-4 py-3 text-center font-bold">
                            Status
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {modalWeeklyPoints.map((item, index) => {
                          const dailyPointValue = Number(
                            item?.dailyPoints || 0,
                          );

                          const totalPointValue = Number(
                            item?.totalPoints || 0,
                          );

                          const isPositive = dailyPointValue >= 0;

                          return (
                            <tr
                              key={item?.pk_performmance_id || index}
                              className="border-t border-gray-100 transition hover:bg-violet-50/40"
                            >
                              <td className="px-4 py-3 font-semibold text-gray-400">
                                {index + 1}
                              </td>

                              <td className="px-4 py-3">
                                <p className="font-bold text-gray-800">
                                  {item?.pk_performmance_id || "N/A"}
                                </p>
                              </td>

                              <td className="px-4 py-3 text-center">
                                <span className="inline-flex min-w-12 justify-center rounded-lg bg-gray-100 px-2 py-1 font-black text-gray-800">
                                  {totalPointValue}
                                </span>
                              </td>

                              <td className="px-4 py-3 text-center">
                                <span
                                  className={`inline-flex min-w-12 justify-center rounded-full px-2.5 py-1 font-black ${
                                    isPositive
                                      ? "bg-emerald-50 text-emerald-700"
                                      : "bg-red-50 text-red-700"
                                  }`}
                                >
                                  {dailyPointValue > 0
                                    ? `+${dailyPointValue}`
                                    : dailyPointValue}
                                </span>
                              </td>

                              <td className="px-4 py-3 text-center">
                                <span
                                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-bold ${
                                    isPositive
                                      ? "bg-emerald-50 text-emerald-700"
                                      : "bg-red-50 text-red-700"
                                  }`}
                                >
                                  <span
                                    className={`h-1.5 w-1.5 rounded-full ${
                                      isPositive
                                        ? "bg-emerald-500"
                                        : "bg-red-500"
                                    }`}
                                  />

                                  {isPositive ? "Added" : "Deducted"}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* =====================================================
                FOOTER
            ===================================================== */}

            <div className="flex shrink-0 items-center justify-between border-t bg-white px-4 py-3">
              <p className="hidden text-[11px] font-medium text-gray-400 sm:block">
                Settlement and performance details for the selected week
              </p>

              <button
                type="button"
                onClick={() => setSelectedSettlement(null)}
                className="ml-auto inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-2 text-xs font-bold text-white transition hover:bg-orange-600"
              >
                <X size={14} />
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =====================================================
          EDIT SETTLEMENT MODAL
      ===================================================== */}

      {editSettlement && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          onClick={closeEditSettlementModal}
        >
          <div
            className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* HEADER */}

            <div className="flex items-center justify-between border-b bg-orange-50 px-5 py-4">
              <div>
                <h3 className="text-base font-black text-gray-900">
                  Update Settlement Amount
                </h3>

                <p className="mt-1 text-xs text-gray-500">
                  {editSettlement.name || "-"} ·{" "}
                  {editSettlement.driver_code || "-"}
                </p>
              </div>

              <button
                type="button"
                onClick={closeEditSettlementModal}
                disabled={updateSettlementLoading}
                className="rounded-full p-2 text-gray-500 transition hover:bg-white hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>

            {/* BODY */}

            <div className="space-y-5 p-5">
              <div className="grid grid-cols-2 gap-3 rounded-2xl bg-gray-50 p-4">
                <div>
                  <p className="text-xs font-semibold text-gray-400">
                    Driver Name
                  </p>

                  <p className="mt-1 text-sm font-bold text-gray-900">
                    {editSettlement.name || "-"}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-400">
                    Driver Code
                  </p>

                  <p className="mt-1 text-sm font-bold text-orange-600">
                    {editSettlement.driver_code || "-"}
                  </p>
                </div>

                {/* DATE */}

                <div>
                  <label
                    htmlFor="edit-date"
                    className="text-xs font-semibold text-gray-400"
                  >
                    Date
                  </label>

                  <input
                    id="edit-date"
                    type="date"
                    value={editDate}
                    onChange={(e) => setEditDate(e.target.value)}
                    className="mt-1 h-9 w-full rounded-lg border border-gray-200 bg-white px-2 text-xs font-semibold text-gray-800 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  />
                </div>

                {/* CURRENT TOTAL */}

                <div className="col-span-2 flex items-center justify-between rounded-lg border border-orange-100 bg-orange-50 px-3 py-2">
                  <div>
                    <p className="text-xs font-semibold text-gray-400">
                      Current Total
                    </p>

                    <p className="mt-0.5 text-sm font-black text-gray-900">
                      {formatAmount(editSettlement.total_amount)}
                    </p>
                  </div>
                </div>
              </div>

              {/* =================================================
                  SETTLEMENT AMOUNT
              ================================================= */}

              <div>
                <label
                  htmlFor="edit-settlement-amount"
                  className="mb-1.5 block text-sm font-bold text-gray-700"
                >
                  Settlement Amount
                </label>

                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 font-semibold text-gray-500">
                    ₹
                  </span>

                  <input
                    id="edit-settlement-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={editSettlementAmount}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (value === "" || Number(value) >= 0) {
                        setEditSettlementAmount(value);
                      }
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="Enter settlement amount"
                    className="h-11 w-full rounded-xl border border-gray-200 pl-8 pr-3 text-sm outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  />
                </div>

                <p className="mt-1 text-xs text-gray-400">
                  Empty chhodne par settlementAmount empty jayega.
                </p>
              </div>

              {/* =================================================
                  PERFORMANCE AMOUNT
              ================================================= */}

              <div>
                <label
                  htmlFor="edit-performance-amount"
                  className="mb-1.5 block text-sm font-bold text-gray-700"
                >
                  Performance Points Amount
                </label>

                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 font-semibold text-gray-500">
                    ₹
                  </span>

                  <input
                    id="edit-performance-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={editPerformanceAmount}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (value === "" || Number(value) >= 0) {
                        setEditPerformanceAmount(value);
                      }
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="Enter performance points amount"
                    className="h-11 w-full rounded-xl border border-gray-200 pl-8 pr-3 text-sm outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  />
                </div>

                <p className="mt-1 text-xs text-gray-400">
                  Empty chhodne par performmancePointsAmount me 0 jayega.
                </p>
              </div>

              {/* =================================================
                  UPDATE SUMMARY
              ================================================= */}

              <div className="rounded-xl border border-orange-100 bg-orange-50 p-4">
                <p className="text-xs font-bold uppercase tracking-wide text-orange-700">
                  Update Summary
                </p>

                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-500">Settlement</p>

                    <p className="mt-1 text-sm font-black text-gray-900">
                      {String(editSettlementAmount).trim() === ""
                        ? "Empty"
                        : formatAmount(editSettlementAmount)}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500">Performance Points</p>

                    <p className="mt-1 text-sm font-black text-gray-900">
                      {formatAmount(editPerformanceAmount || 0)}
                    </p>
                  </div>
                </div>
              </div>

              {/* =================================================
                  ACTIONS
              ================================================= */}

              <div className="flex justify-end gap-3 border-t pt-4">
                <button
                  type="button"
                  onClick={closeEditSettlementModal}
                  disabled={updateSettlementLoading}
                  className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-bold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleUpdateSettlement}
                  disabled={updateSettlementLoading}
                  className="inline-flex min-w-32 items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {updateSettlementLoading ? (
                    <>
                      <Loader size={16} className="animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      Update
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =====================================================
          ADD EMPLOYEE MODAL
      ===================================================== */}

      {employeeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative h-[90vh] w-full max-w-6xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            {/* HEADER */}

            <div className="flex items-center justify-between border-b bg-orange-50 px-5 py-3">
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  Add Employee
                </h3>

                <p className="text-xs text-gray-500">
                  Fill employee details from the embedded form.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setEmployeeModalOpen(false)}
                className="rounded-full p-2 text-gray-500 hover:bg-white hover:text-red-500"
              >
                <X size={20} />
              </button>
            </div>

            {/* IFRAME */}

            <iframe
              title="Add Employee Form"
              src="https://billpunch.com/widget/employeeform/"
              className="h-[calc(90vh-58px)] w-full border-0"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default SettlementModals;
